import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, ExternalLink, Tag } from 'lucide-react';
import { getMaterial, getCategories } from '../api';
import { useTelegram } from '../context/TelegramContext';

const MaterialScreen = () => {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const { openLink, showAlert } = useTelegram();
  const [material, setMaterial] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const materialData = await getMaterial(materialId);
        setMaterial(materialData);

        if (materialData) {
          const categoriesData = await getCategories();
          const currentCategory = categoriesData.find(cat => cat.id === materialData.category);
          setCategory(currentCategory);
        }
        
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (materialId) {
      fetchData();
    }
  }, [materialId]);

  const handleGoToMaterial = () => {
    if (material?.link) {
      openLink(material.link);
    } else {
      showAlert('Ссылка на материал недоступна');
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Загрузка...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">Ошибка: {error}</div>;
  }

  if (!material || !category) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Материал не найден
          </h2>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Вернуться назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Заголовок */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-md mx-auto p-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(`/category/${category.id}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {category.name}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Контент материала */}
      <div className="max-w-md mx-auto p-4">
        <div className="card overflow-hidden">
          {/* Обложка */}
          <div className="relative">
            <img
              src={material.image}
              alt={material.title}
              className="w-full h-48 object-cover"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/400x200?text=📄';
              }}
            />
            <div className="absolute top-4 left-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${category.color}`}>
                {category.name}
              </span>
            </div>
          </div>

          {/* Информация */}
          <div className="p-6">
            <h1 className="text-xl font-bold text-gray-900 mb-3">
              {material.title}
            </h1>
            
            <p className="text-gray-600 mb-4 leading-relaxed">
              {material.description}
            </p>

            {/* Метаданные */}
            <div className="space-y-3 mb-6">
              {material.duration && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Длительность: {material.duration}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>Дата: {new Date(material.date).toLocaleDateString('ru-RU')}</span>
              </div>
            </div>

            {/* Теги */}
            {material.tags && material.tags.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Теги:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {material.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gold-100 text-gold-700 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Кнопка перехода */}
            <button
              onClick={handleGoToMaterial}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3"
            >
              <ExternalLink className="w-4 h-4" />
              Перейти к материалу
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialScreen; 